import { useState, useEffect, useCallback, useRef } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import './Module2World.scss'
import '../../Animations.css'

import MediaService from '../../services/MediaService'
import { getVideo } from '../../s3Func/s3Download'

import Practice from './ListeningPractice'
import Module2Config from './Module2Config'
import { getUserContent, sleep } from '../../utils'
import WorldContainer from '../../components/World/WorldContainer'

/**
 * Module1World is a base template for all worlds that follow it. In this case, this is for the
 * beach scene, but this code has been copied and pasted directly to worlds 1-5 with only slight
 * name changes to apply to each world. After world 5, there is an additional component of
 * Follow The Beat, which will have a comment writeup in Module6World.
 * @returns The HTML code for Module1World
 */
const Module2World = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	let { module2 } = useSelector((state) => state.module2, shallowEqual)
	const { login } = useSelector((state) => state.loginReducer, shallowEqual)
	const [slidesConfig, setSlidesConfig] = useState({})
	const [displayPracticeVideo, setDisplayPracticeVideo] = useState(false)
	const [showRewardSelection, setShowRewardSelection] = useState(false)
	const [showFinalAnimation, setShowFinalAnimation] = useState(false)
	const [dogCelebrateGif, setDogCelebrateGif] = useState('')
	const [dogCelebrateAudio, setDogCelebrateAudio] = useState('')
	const [showDiggingAnimation, setShowDiggingAnimation] = useState(false)
	const [dogDigGif, setDogDigGif] = useState(null)
	const [dogDigAudio, setDogDigAudio] = useState(null)
	const [displayEndingVideo, setDisplayEndingVideo] = useState(false)

	// Always-mounted audio element for world sounds (iOS-safe)
	const worldAudioRef = useRef(null)
	const expectedWorldSrcRef = useRef('')
	const worldEndActionRef = useRef(null)
	const [worldAudioSrc, setWorldAudioSrc] = useState('')
	const [worldShouldPlay, setWorldShouldPlay] = useState(false)

	const WORLD_IMAGES = {
		progressBar0: '/Module2Images/EmptyBone.png',
		progressBar1: '/Module2Images/Bone1outof3.png',
		progressBar2: '/Module2Images/Bone2outof3.png',
		progressBarCompleted: '/Module2Images/Bone3outof3.png',
		keyReward: '/Module2Images/Bone3outof3.png',

		gift: '/Images/Gift.png',

		dirtCloud: '/Module2Images/Dirt+cloud.png',

		animal: '/Module2Images/Dog+Still.gif',
		worldBackground: '/Module2Images/U1M2_DayScene_Landscape.png',
		logoutBtn: '/Module1Images/Logout.png',
		journeyImage: '/Module1Images/bluejourneymap.svg',
	}

	const WORLD_AUDIO = {
		rewardFound: '/Module2Videos/daily_rewards_present.mp3',
	}

	const WORLD_REWARDS = {
		Ball: '/Module2Images/Reward_Ball.png',
		ButterflyBlue: '/Module2Images/Reward_ButterflyBlue.png',
		ButterflyPink: '/Module2Images/Reward_ButterflyPink.png',
		Flowers: '/Module2Images/Reward_Flowers.png',
		KitePink: '/Module2Images/Reward_KitePink.png',
		KitePurple: '/Module2Images/Reward_KitePurple.png',
		MerryGoRound: '/Module2Images/Reward_MerryGoAround.png',
		Pond: '/Module2Images/Reward_Pond.png',
		Sandbox: '/Module2Images/Reward_SandPit.png',
		SpringToy: '/Module2Images/Reward_BikeSpringChair.png',
		Swingset: '/Module2Images/Reward_Swing.png',
		TireSwing: '/Module2Images/Reward_TireSwing.png',
		Bow: '/Module2Images/Reward_Bow.png',
		Hat: '/Module2Images/Reward_Hat.png',
		MonkeybarsBlue: '/Module2Images/Reward_MonkeyBarsBlue.png',
		MonkeybarsYellow: '/Module2Images/Reward_MonkeyBarYellow.png',
		SlideRed: '/Module2Images/Reward_RedSlide.png',
		SlideYellow: '/Module2Images/Reward_YellowSlide.png',
	}

	const SLIDE_BAR = {
		MonkeybarsBlue: '/Module2Images/Reward_MonkeyBarsBlue.png',
		MonkeybarsYellow: '/Module2Images/Reward_MonkeyBarYellow.png',
		SlideRed: '/Module2Images/Reward_RedSlide.png',
		SlideYellow: '/Module2Images/Reward_YellowSlide.png',
	}

	const CHARACTER_REWARDS = {
		Bow: '/Module2Images/Reward_Bow.png',
		Hat: '/Module2Images/Reward_Hat.png',
	}

	// Get AWS URL World Images
	Object.keys(WORLD_IMAGES).forEach((key) => {
		WORLD_IMAGES[key] = MediaService.getImage(WORLD_IMAGES[key], login)
	})

	// Get AWS URL SLIDE_BAR Images
	Object.keys(SLIDE_BAR).forEach((key) => {
		SLIDE_BAR[key] = MediaService.getImage(SLIDE_BAR[key], login)
	})

	// Get AWS URL Reward Images
	Object.keys(CHARACTER_REWARDS).forEach((key) => {
		CHARACTER_REWARDS[key] = MediaService.getImage(
			CHARACTER_REWARDS[key],
			login
		)
	})

	// Get AWS URL Reward Images
	Object.keys(WORLD_REWARDS).forEach((key) => {
		WORLD_REWARDS[key] = MediaService.getImage(WORLD_REWARDS[key], login)
	})

	// Get AWS URL World Audio
	Object.keys(WORLD_AUDIO).forEach((key) => {
		WORLD_AUDIO[key] = MediaService.getVideo(WORLD_AUDIO[key], login)
	})

	const createListeningSlideShow = useCallback(async () => {
		const config = {
			navigate: '/listening',
			json: '',
			song: '',
			recording: '',
		}

		const { learn } = Module2Config[`day${module2.currentday}`]

		if (learn) {
			if (module2.currentday <= 4) {
				config.song = await getVideo(learn.song, login)
			} else {
				const userRecording = await getUserContent(
					learn.recording,
					module2.StudentID,
					login.influencerid,
					login
				  );
				const song = await getUserContent(
				learn.song,
				module2.StudentID,
				login.influencerid,
				login
				);
		
				const demoSong = await getUserContent(
				learn.demoSong,
				module2.StudentID,
				login.influencerid,
				login
				);
				fetch(userRecording).then((response) => {
				if (response.status === 200) {
					config.recording = userRecording;
					config.song = song;
				}
				else{
					config.recording = null;
					config.song = demoSong;
				}
		
				})
			}

			config.json = learn.json
		}

		setSlidesConfig(config)
	}, [])

	const getDogAnimation = async () => {
		const gifDigData = await getVideo('appfiles/listening/dog_dig.gif', login)
		const audioDigData = await getVideo('appfiles/listening/dog_digging.mp3', login)
		const gifAmazing = await getVideo('appfiles/listening/dog_amazing.gif', login)
		const audioAmazing = await getVideo('appfiles/listening/listening_day_finished.mp3', login)

		setDogDigGif(gifDigData)
		setDogDigAudio(audioDigData)

		setDogCelebrateGif(gifAmazing)
		setDogCelebrateAudio(audioAmazing)
	}

	const handleFinalAnimationEnd = async () => {
		setShowFinalAnimation(false)
		await sleep(500)		
		dispatch({
			type: `MODULE2_SET_DAY_UNLOCK${module2.currentday + 1}`,
			payload: true,
		})

		dispatch({
			type: 'MODULE2_SET_dayCompleted',
			payload: module2.currentday,
		})

		if (module2.currentday !== 9) {
			dispatch({
				type: 'MODULE2_SET_stepCompleted',
				payload: 'notstarted',
			})
			await sleep(100);
			navigate('/listening/navigation')
		} else {
			setDisplayEndingVideo(true)
		}
	}

	useEffect(() => {
		createListeningSlideShow()
		getDogAnimation()
	}, [module2.stepComplete, createListeningSlideShow])

	const rewardSelectionAnimation = async () => {
		await sleep(1000)
		setShowDiggingAnimation(true)
	}

	const handleAnimationEnded = () => {
		setShowDiggingAnimation(false)
		setShowRewardSelection(true)
	}

	const playWorldAudio = useCallback(async (src, onEndedAction = null, restartIfSame = true) => {
		if (src && typeof src.then === 'function') {
			src = await src
		}
		const el = worldAudioRef.current
		const currentSrc = el ? (el.currentSrc || el.src) : ''
		const same = !!currentSrc && currentSrc.includes(src)
		expectedWorldSrcRef.current = src
		worldEndActionRef.current = onEndedAction
		if (el && same) {
			if (!restartIfSame) return
			try {
				el.pause()
				el.currentTime = 0
				await el.play()
			} catch (e) {
				const msg = String(e && e.message ? e.message : e)
				if (!msg.includes('interrupted by a new load request')) {
					console.log('world audio replay failed', e)
				}
			}
		} else {
			setWorldAudioSrc(src)
			setWorldShouldPlay(true)
		}
	}, [])

	useEffect(() => {
		if (showDiggingAnimation && dogDigAudio) {
			playWorldAudio(dogDigAudio, handleAnimationEnded)
		}
	}, [showDiggingAnimation, dogDigAudio, handleAnimationEnded, playWorldAudio])

	useEffect(() => {
		if (showFinalAnimation && dogCelebrateAudio) {
			playWorldAudio(dogCelebrateAudio, handleFinalAnimationEnd)
		}
	}, [showFinalAnimation, dogCelebrateAudio, handleFinalAnimationEnd, playWorldAudio])

	return (
		<>
			<WorldContainer
				name={"Listening"}
				module={module2}
				moduleName="MODULE2"
				moduleConfig={Module2Config}
				slidesConfig={slidesConfig}
				keyIcon={'dog-bone'}
				practice={
					<Practice
						onPracticeClose={()=>{setDisplayPracticeVideo(false); module2.sectionCompleted = module2.sectionCompleted + 1}}
						background={WORLD_IMAGES.worldBackground}
						stepComplete="MODULE2_SET_stepCompleted"
						module={module2}
					/>
				}
				route="/listening"
				worldImages={WORLD_IMAGES}
				worldRewards={WORLD_REWARDS}
				showRewardSelection={showRewardSelection}
				uuid="listening"
				onPracticeClick={() => setDisplayPracticeVideo(true)}
				displayPracticeVideo={displayPracticeVideo}
				onRewardKeyClick={rewardSelectionAnimation}
				onRewardSelected={() => (setShowFinalAnimation(true), setShowRewardSelection(false))}
				displayEndingVideo={displayEndingVideo}
			>
				<div className="slide-bar">
					{module2.rewards.SlideRed && (
						<img
							alt=""
							className="slide-bar MODULE2 SlideRed"
							src={SLIDE_BAR.SlideRed}
						/>
					)}
					{module2.rewards.SlideYellow && (
						<img
							alt=""
							className="slide-bar MODULE2 SlideYellow"
							src={SLIDE_BAR.SlideYellow}
						/>
					)}

					{module2.rewards.MonkeybarsBlue && (
						<img
							alt=""
							className="slide-bar MODULE2 MonkeybarsBlue"
							src={SLIDE_BAR.MonkeybarsBlue}
						/>
					)}
					{module2.rewards.MonkeybarsYellow && (
						<img
							alt=""
							className="slide-bar MODULE2 MonkeybarsYellow"
							src={SLIDE_BAR.MonkeybarsYellow}
						/>
					)}

				</div>
				<div className="dog-character">
					<figure>
						{module2.rewards.Hat && (
							<img
								alt=""
								className="character-rewards MODULE2 Hat"
								src={CHARACTER_REWARDS.Hat}
							/>
						)}
						{module2.rewards.Bow && (
							<img
								alt=""
								className="character-rewards MODULE2 Bow"
								src={CHARACTER_REWARDS.Bow}
							/>
						)}
						<img
							style={{
								opacity: !showDiggingAnimation && !showFinalAnimation ? 1 : 0,
							}}
							className="dog-standing"
							src={WORLD_IMAGES.animal}
							alt="dog standing"
						/>

						<span style={{ opacity: showFinalAnimation ? 1 : 0 }}>
							<img
								className="dog-amazing"
								src={dogCelebrateGif}
								alt="dog amazing"
							/>
						</span>
						<span
							style={{ opacity: showDiggingAnimation ? 1 : 0 }}
						>
							<img className="dog-digging" src={dogDigGif} alt="dog digging" />
							<img
								className="dog-dust"
								id="dirtAnimation"
								src={WORLD_IMAGES.dirtCloud}
								alt="dog dust"
							/>
						</span>
					</figure>
				</div>
			</WorldContainer>
		{/* Always-mounted world audio element */}
		<audio
			ref={worldAudioRef}
			src={worldAudioSrc}
			preload='auto'
			playsInline
			onLoadedData={() => {
				const el = worldAudioRef.current
				if (!el) return
				const expected = expectedWorldSrcRef.current
				const currentSrc = el.currentSrc || el.src
				if (!expected || (currentSrc && !currentSrc.includes(expected))) return
				if (!worldShouldPlay) return
				el.currentTime = 0
				el.play().catch((e) => {
					const msg = String(e && e.message ? e.message : e)
					if (!msg.includes('interrupted by a new load request')) {
						console.log('world audio play failed', e)
					}
				})
			}}
			onEnded={() => {
				const action = worldEndActionRef.current
				worldEndActionRef.current = null
				setWorldShouldPlay(false)
				if (typeof action === 'function') action()
			}}
		/>
		</>
	)
}
export default Module2World
